/**
 * Utility functions to calculate statistics from manual task API responses
 */

/**
 * Calculate the percentage of tasks that are on track with their SLA
 * @param {Array} tasks - Array of task objects from API
 * @returns {number} Percentage of tasks on track (0-100)
 */
export const calculateSLAOnTrackPercentage = (tasks) => {
  const tasksWithSLA = tasks.filter(t => t.sla_minutes !== null && t.due_at !== null);
  
  if (tasksWithSLA.length === 0) return 0;
  
  const onTrackTasks = tasksWithSLA.filter(t => new Date(t.due_at) > new Date());
  return Math.round((onTrackTasks.length / tasksWithSLA.length) * 100);
};

/**
 * Count claims nearing SLA (within 24 hours)
 * @param {Array} tasks - Array of task objects from API
 * @returns {number} Count of claims nearing SLA
 */
export const countClaimsNearingSLA = (tasks) => {
  return tasks.filter(task => {
    if (task.sla_minutes && task.due_at) {
      const hoursRemaining = (new Date(task.due_at) - new Date()) / (1000 * 60 * 60);
      return hoursRemaining > 0 && hoursRemaining <= 24;
    } else {
      const hoursSinceCreation = (new Date() - new Date(task.created_at)) / (1000 * 60 * 60);
      return hoursSinceCreation >= 0 && hoursSinceCreation <= 24;
    }
  }).length;
};

/**
 * Count cases assigned to current user
 * @param {Array} tasks - Array of task objects from API
 * @param {string} currentUserEmail - Email of the current user
 * @returns {number} Count of user's cases
 */
export const countYourCases = (tasks, currentUserEmail) => {
  return tasks.filter(t => 
    t.assigned_user_email === currentUserEmail || 
    t.user_id === currentUserEmail
  ).length;
};

/**
 * Calculate overall average TAT (Turnaround Time) in hours
 * @param {Array} tasks - Array of task objects from API
 * @returns {number} Average TAT in hours
 */
export const calculateOverallTAT = (tasks) => {
  const completedTasks = tasks.filter(t => t.finished_at && t.started_at);
  
  if (completedTasks.length === 0) return 0;
  
  const totalMinutes = completedTasks.reduce((sum, task) => {
    const tatMinutes = (new Date(task.finished_at) - new Date(task.started_at)) / (1000 * 60);
    return sum + tatMinutes;
  }, 0);
  
  return Math.round((totalMinutes / completedTasks.length) / 60 * 10) / 10; // Convert to hours, round to 1 decimal
};

/**
 * Get list of critical claims
 * @param {Array} tasks - Array of task objects from API
 * @returns {Array} Array of critical task objects
 */
export const getCriticalClaims = (tasks) => {
  return tasks.filter(task => {
    const severity = task.input?.statusSeverity;
    const hasError = task.session_state?.workflow_vars?.transaction_status === "ERROR";
    const isOverdue = task.due_at && new Date(task.due_at) < new Date();
    const hasErrorMessage = task.input?.error_msg;
    
    return severity === "error" || hasError || isOverdue || hasErrorMessage;
  });
};

/**
 * Get list of claims that need attention
 * @param {Array} tasks - Array of task objects from API
 * @returns {Array} Array of task objects needing attention
 */
export const getNeedAttentionClaims = (tasks) => {
  return tasks.filter(task => {
    const severity = task.input?.statusSeverity;
    
    // Check if nearing SLA
    let nearSLA = false;
    if (task.sla_minutes && task.due_at) {
      const hoursRemaining = (new Date(task.due_at) - new Date()) / (1000 * 60 * 60);
      nearSLA = hoursRemaining > 0 && hoursRemaining <= 24;
    } else {
      const hoursSinceCreation = (new Date() - new Date(task.created_at)) / (1000 * 60 * 60);
      nearSLA = hoursSinceCreation >= 0 && hoursSinceCreation <= 24;
    }
    
    // Check data completeness
    const completenessText = task.input?.primaryMetricValueText;
    const lowCompleteness = completenessText ? parseFloat(completenessText.replace('%', '')) < 70 : false;
    
    return severity === "warning" || nearSLA || lowCompleteness;
  });
};

/**
 * Get list of pending tasks
 * @param {Array} tasks - Array of task objects from API
 * @returns {Array} Array of pending task objects
 */
export const getPendingTasks = (tasks) => {
  return tasks.filter(t => t.status === "WAITING_FOR_MANUAL");
};

/**
 * Calculate all statistics at once
 * @param {Array} tasks - Array of task objects from API
 * @param {string} currentUserEmail - Email of the current user
 * @returns {Object} Object containing all statistics
 */
export const calculateAllStatistics = (tasks, currentUserEmail = null) => {
  return {
    slaOnTrackPercentage: calculateSLAOnTrackPercentage(tasks),
    claimsNearingSLA: countClaimsNearingSLA(tasks),
    yourCases: currentUserEmail ? countYourCases(tasks, currentUserEmail) : 0,
    overallTAT: calculateOverallTAT(tasks),
    nearSLA24Hours: countClaimsNearingSLA(tasks), // Same as claimsNearingSLA
    criticalClaims: getCriticalClaims(tasks),
    needAttentionClaims: getNeedAttentionClaims(tasks),
    pendingTasks: getPendingTasks(tasks),
    
    // Counts for easy display
    criticalCount: getCriticalClaims(tasks).length,
    needAttentionCount: getNeedAttentionClaims(tasks).length,
    pendingCount: getPendingTasks(tasks).length,
    totalTasks: tasks.length
  };
};

/**
 * Format time remaining for display
 * @param {Object} task - Task object
 * @returns {string} Formatted time remaining
 */
export const formatTimeRemaining = (task) => {
  if (task.due_at) {
    const hoursRemaining = (new Date(task.due_at) - new Date()) / (1000 * 60 * 60);
    
    if (hoursRemaining < 0) {
      return `Overdue by ${Math.abs(Math.round(hoursRemaining))}h`;
    } else if (hoursRemaining < 24) {
      return `${Math.round(hoursRemaining)}h remaining`;
    } else {
      const days = Math.floor(hoursRemaining / 24);
      return `${days}d remaining`;
    }
  } else {
    // No SLA - show time since creation
    const hoursSinceCreation = (new Date() - new Date(task.created_at)) / (1000 * 60 * 60);
    
    if (hoursSinceCreation < 24) {
      return `${Math.round(hoursSinceCreation)}h ago`;
    } else {
      const days = Math.floor(hoursSinceCreation / 24);
      return `${days}d ago`;
    }
  }
};

/**
 * Get priority/severity badge info for a task
 * @param {Object} task - Task object
 * @returns {Object} Object with severity level and color
 */
export const getTaskSeverity = (task) => {
  const severity = task.input?.statusSeverity;
  const hasError = task.session_state?.workflow_vars?.transaction_status === "ERROR";
  const isOverdue = task.due_at && new Date(task.due_at) < new Date();
  
  if (severity === "error" || hasError || isOverdue) {
    return { level: "critical", color: "#ff3e42", label: "Critical" };
  } else if (severity === "warning") {
    return { level: "warning", color: "#f59e0b", label: "Warning" };
  } else if (severity === "success") {
    return { level: "success", color: "#10b981", label: "On Track" };
  } else {
    return { level: "neutral", color: "#6b7280", label: "Pending" };
  }
};
